import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  Popper,
  Snackbar,
  SnackbarProps,
  Stack,
  SxProps,
  TextFieldProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import PhoneInput from 'react-phone-number-input';
import { Moment } from 'moment';
import 'react-phone-number-input/style.css';

import { useNavigate } from 'react-router';
import {
  getCurriculumV2State,
  getCurriculumsV2,
} from '../../store/V2/CurriculumSlice';
import { getAuthState } from '../../store/AuthSlice';
import { TUserSignUp, userSignUp } from '../../store/AuthSlice/V2/AuthApi';
import { Countries, GRADIENT_COLORS, TP_COLORS } from '../../constants';
import { useAppSelector } from '../../hooks';
import { isEmailValid } from '../../helper';
import {
  TPTextField,
  TPAutoCompleteArrowIconComponent,
} from '../../components/Inputs';
import HeaderBar from './components/HeaderBar';

type TSelectOption = {
  label: string;
  value: string;
};

type TState = {
  name: string;
  email: string;
  phoneNumber: string;
  password: string;
  examDate: Moment | null;
  selectedCurriculum: TSelectOption | null;
  selectedGrade: TSelectOption | null;
  isPasswordVisible?: boolean;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const containerStyles: SxProps = {
  background:
    'linear-gradient(107.83deg, #003768 7.88%, #006C9C 41.91%, #39BFB9 82.16%)',
  minHeight: '100vh',
};

const centeredContainerStyles: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 'calc(100vh - 79px)',
  paddingBottom: '15px',
};

const contentContainerStyles: SxProps = {
  borderRadius: '30px',
  padding: '32px',
  backgroundColor: TP_COLORS.white,
};

const snackbarProps: SnackbarProps = {
  anchorOrigin: { vertical: 'top', horizontal: 'center' },
  autoHideDuration: 10000,
  message: (
    <Typography
      fontWeight={600}
      sx={{
        backgroundColor: TP_COLORS.gray.darken,
        borderRadius: '30px',
        padding: '11px 30px',
      }}
    >
      Sign up now to activate and enjoy your free trial - don&apos;t miss out!
    </Typography>
  ),
  ContentProps: {
    sx: {
      padding: '2px',
      borderRadius: '30px',
      background: GRADIENT_COLORS.color1,
      '.MuiSnackbarContent-message': {
        padding: 0,
      },
    },
  },
};

const defaultErrorData = {
  visible: false,
  message: '',
};

const CustomPopperComponent = (props) => {
  return <Popper {...props} style={{ width: '300px' }} />;
};

const deviceTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  ? Intl.DateTimeFormat().resolvedOptions().timeZone
  : 'Asia/Singapore';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let defaultCountryCode: any = 'SG';

const defaultUserTimezone = Countries.find(
  (country) => country.value === deviceTimeZone
);

if (defaultUserTimezone && defaultUserTimezone.countryCode) {
  defaultCountryCode = defaultUserTimezone.countryCode;
}

const initialState: TState = {
  name: '',
  email: '',
  phoneNumber: '',
  password: '',
  examDate: null,
  selectedCurriculum: null,
  selectedGrade: null,
  isPasswordVisible: false,
};

const SignUp: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Store
  const curriculumState = useAppSelector(getCurriculumV2State);
  const authState = useAppSelector(getAuthState);

  // State
  const [state, setState] = useState(initialState);
  const [errors, setErrors] = useState({
    name: { ...defaultErrorData },
    email: { ...defaultErrorData },
    phoneNumber: { ...defaultErrorData },
    password: { ...defaultErrorData },
  });

  const [gradeOptions, setGradeOptions] = useState<Array<TSelectOption>>([]);
  const [curriculumOptions, setCurriculumOptions] = useState<
    Array<TSelectOption>
  >([]);

  const authUser = authState.user;
  const isLgOrMore = useMediaQuery(theme.breakpoints.up('lg'));
  const isSubmitDisabled =
    !state.name.trim().length ||
    errors.name.visible ||
    !state.email.trim().length ||
    errors.email.visible ||
    !state.phoneNumber.trim().length ||
    errors.phoneNumber.visible ||
    !state.password.trim().length ||
    errors.password.visible ||
    !state.selectedCurriculum ||
    !state.selectedGrade ||
    authState.status === 'loading';

  useEffect(() => {
    if (authState.accessToken && authUser) {
      if (authUser && authUser.role && authUser.role.type === 'admin') {
        navigate(`${process.env.PUBLIC_URL}/dashboard`, { replace: true });
      } else if (authUser && authUser.role && authUser.role.name !== 'Guest') {
        navigate(`${process.env.PUBLIC_URL}`, { replace: true });
      }
    }

    dispatch(getCurriculumsV2());
  }, []);

  useEffect(() => {
    if (state.name) {
      let _nameError = { ...defaultErrorData };

      if (state.name.length < 3) {
        _nameError = {
          visible: true,
          message: 'Name should have at least 3 characters.',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        name: _nameError,
      }));
    }
  }, [state.name]);

  useEffect(() => {
    if (state.email) {
      let _emailError = { ...defaultErrorData };

      if (!isEmailValid(state.email)) {
        _emailError = {
          visible: true,
          message: 'Invalid Email.',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        email: _emailError,
      }));
    }
  }, [state.email]);

  useEffect(() => {
    if (state.password) {
      let _passwordError = { ...defaultErrorData };

      if (state.password.length < 8) {
        _passwordError = {
          visible: true,
          message: 'Password should have atleast 8 characters.',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        password: _passwordError,
      }));
    }
  }, [state.password]);

  useEffect(() => {
    if (state.phoneNumber) {
      let _phoneNumberError = { ...defaultErrorData };

      if (state.phoneNumber.length < 9) {
        _phoneNumberError = {
          visible: true,
          message: 'Phone number should have atleast 8 characters.',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        phoneNumber: _phoneNumberError,
      }));
    }
  }, [state.phoneNumber]);

  useEffect(() => {
    if (curriculumState.list && curriculumState.list.length) {
      setCurriculumOptions(
        curriculumState.list.map((item) => ({
          label: item.curriculum,
          value: item.curriculum,
        }))
      );
    }
  }, [curriculumState.list]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;

    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handlePhoneNumberChange = (value: string) => {
    setState((prevState) => ({
      ...prevState,
      phoneNumber: value || '',
    }));
  };

  // const handleExamDateChange = (date: Moment | null) => {
  //   setState((prevState) => ({
  //     ...prevState,
  //     examDate: date,
  //   }));
  // };

  const togglePasswordVisibility = () => {
    setState((prevState) => ({
      ...prevState,
      isPasswordVisible: !prevState.isPasswordVisible,
    }));
  };

  const handleCurriculumChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    const newGradeOptions: TSelectOption[] = [];

    if (curriculumState.list && curriculumState.list.length && value) {
      const selectedCurriculumData = curriculumState.list.find(
        (curriculumItem) => curriculumItem.curriculum === value.value
      );

      if (
        selectedCurriculumData &&
        selectedCurriculumData.subjects &&
        selectedCurriculumData.subjects.length
      ) {
        selectedCurriculumData.subjects.forEach((subjectItem) => {
          if (subjectItem.grade && subjectItem.grade.length) {
            subjectItem.grade.forEach((gradeItem) => {
              const isAlreadyIncluded = newGradeOptions.some(
                (option) => option.value === gradeItem.grade
              );

              if (!isAlreadyIncluded) {
                newGradeOptions.push({
                  label: gradeItem.grade,
                  value: gradeItem.grade,
                });
              }
            });
          }
        });
      }
    }

    setState((prevState) => ({
      ...prevState,
      selectedCurriculum: value,
      selectedGrade: null,
    }));

    setGradeOptions(newGradeOptions);
  };

  const handleGradeChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedGrade: value,
    }));
  };

  const onSignUp = () => {
    if (!isSubmitDisabled && state.selectedCurriculum && state.selectedGrade) {
      const apiData: TUserSignUp = {
        name: state.name,
        email: state.email,
        phoneNumber: state.phoneNumber,
        password: state.password,
        timezone: deviceTimeZone,
        curriculum: state.selectedCurriculum.value,
        grade: state.selectedGrade.value,
        clientId: process.env.REACT_APP_TUTOPIYA_CLIENT_ID || '',
        clientSecret: process.env.REACT_APP_TUTOPIYA_CLIENT_SECRET || '',
      };

      if (state.examDate) {
        apiData.examDate = state.examDate.toDate().getTime();
      }

      dispatch(userSignUp(apiData));
    }
  };

  return (
    <Box sx={containerStyles}>
      <HeaderBar />
      <Box sx={centeredContainerStyles}>
        <Container maxWidth="lg">
          <Box sx={contentContainerStyles}>
            <Grid container spacing={2} alignItems="center">
              {isLgOrMore ? (
                <Grid item xs={12} sm={12} md={12} lg={6}>
                  <div style={{ marginBottom: '15px', marginTop: '60px' }}>
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      color="#006C9C"
                      lineHeight="48.41px"
                      fontSize="40px"
                    >
                      Join Us &
                      <br />
                      <span style={{ color: '#01A79F' }}>
                        Elevate Your Education
                      </span>
                    </Typography>
                  </div>
                  <div>
                    <Typography
                      fontWeight={500}
                      fontSize="16px"
                      lineHeight="19.36px"
                      color="#334155"
                      maxWidth="464px"
                    >
                      Make learning easy and hassle free by practicing our study
                      quizzes and using our resources to boost your confidence
                      and achieve exam success!
                    </Typography>
                  </div>
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    marginTop="-140px"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/images/sign-up.png`}
                      alt="tp-logo"
                      style={{ width: '100%', maxWidth: '595px' }}
                    />
                  </Stack>
                </Grid>
              ) : null}
              <Grid item xs={12} sm={12} md={12} lg={6}>
                <div
                  style={{
                    maxWidth: '400px',
                    margin: '0 auto',
                  }}
                >
                  <div style={{ marginBottom: '10px' }}>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      fontSize="30px"
                      lineHeight="36.3px"
                      textAlign="center"
                      color={TP_COLORS.black}
                    >
                      Register With Us
                    </Typography>
                  </div>
                  <div style={{ marginBottom: '30px' }}>
                    <Typography
                      variant="h6"
                      fontWeight={400}
                      fontSize="14px"
                      lineHeight="17px"
                      textAlign="center"
                      color={TP_COLORS.black}
                    >
                      Unlock a world of possibilities! Sign up now to access
                      exclusive features.
                    </Typography>
                  </div>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      onSignUp();
                    }}
                  >
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <TPTextField
                            {...textFieldProps}
                            name="name"
                            label="Full Name *"
                            value={state.name}
                            onChange={handleInputChange}
                            placeholder="Full Name"
                            error={errors.name.visible}
                            helperText={errors.name.message}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <TPTextField
                            {...textFieldProps}
                            name="email"
                            label="Email Address *"
                            value={state.email}
                            onChange={handleInputChange}
                            placeholder="Email Address"
                            error={errors.email.visible}
                            helperText={errors.email.message}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <PhoneInput
                          international
                          defaultCountry={defaultCountryCode}
                          placeholder="Enter phone number"
                          name="phoneNumber"
                          value={state.phoneNumber}
                          onChange={handlePhoneNumberChange}
                          className="phone-number-input-custom-styles"
                          style={{
                            height: '52px',
                            backgroundColor: '#F1F5F9',
                          }}
                          numberInputProps={{
                            style: {
                              height: '52px',
                              borderColor: errors.phoneNumber.visible
                                ? '#FF2B2D'
                                : '#CBD5E1',
                              fontFamily:
                                "'Roboto','Helvetica','Arial',sans-serif",
                              fontWeight: 400,
                              fontSize: '1rem',
                              lineHeight: '1.4375em',
                            },
                          }}
                        />
                        {errors.phoneNumber.message ? (
                          <FormHelperText error style={{ marginLeft: '14px' }}>
                            {errors.phoneNumber.message}
                          </FormHelperText>
                        ) : null}
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <TPTextField
                            {...textFieldProps}
                            type={state.isPasswordVisible ? 'text' : 'password'}
                            name="password"
                            label="Password *"
                            value={state.password}
                            onChange={handleInputChange}
                            placeholder="Password"
                            error={errors.password.visible}
                            helperText={errors.password.message}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={togglePasswordVisibility}
                                  >
                                    {state.isPasswordVisible ? (
                                      <VisibilityOffIcon />
                                    ) : (
                                      <VisibilityIcon />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </FormControl>
                      </Grid>
                      {/* <Grid item xs={12}>
                        <FormControl fullWidth>
                          <LocalizationProvider dateAdapter={AdapterMoment}>
                            <MobileDatePicker
                              clearable
                              value={state.examDate}
                              minDate={moment()}
                              onChange={handleExamDateChange}
                              label="Exam Sitting"
                              inputFormat="YYYY MMM DD"
                              renderInput={(params) => (
                                <TPTextField
                                  {...params}
                                  {...textFieldProps}
                                  size="small"
                                  placeholder="Exam Sitting"
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <DateRangeIcon />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              )}
                            />
                          </LocalizationProvider>
                        </FormControl>
                      </Grid> */}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <Autocomplete
                            size="small"
                            value={state.selectedCurriculum}
                            options={curriculumOptions}
                            onChange={handleCurriculumChange}
                            popupIcon={TPAutoCompleteArrowIconComponent}
                            PopperComponent={CustomPopperComponent}
                            // loading={curriculumState.status === 'loading'}
                            isOptionEqualToValue={(option, value) =>
                              option.label === value.value
                            }
                            renderInput={(params) => (
                              <TPTextField
                                {...params}
                                {...textFieldProps}
                                size="small"
                                label="Curriculum *"
                                placeholder="Curriculum"
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <Autocomplete
                            disabled={!state.selectedCurriculum}
                            size="small"
                            value={state.selectedGrade}
                            options={gradeOptions}
                            onChange={handleGradeChange}
                            popupIcon={TPAutoCompleteArrowIconComponent}
                            PopperComponent={CustomPopperComponent}
                            isOptionEqualToValue={(option, value) =>
                              option.label === value.value
                            }
                            renderInput={(params) => (
                              <TPTextField
                                {...params}
                                {...textFieldProps}
                                size="small"
                                label="Grade *"
                                placeholder="Grade"
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <LoadingButton
                          fullWidth
                          type="submit"
                          size="large"
                          variant="contained"
                          color="primary"
                          disabled={isSubmitDisabled}
                          loading={authState.status === 'loading'}
                        >
                          Sign Up
                        </LoadingButton>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          fontWeight={500}
                          color="#647488"
                          fontSize="14px"
                          lineHeight="16.94px"
                          textAlign="center"
                        >
                          Already have an account ?{' '}
                          <NavLink
                            to={`${process.env.PUBLIC_URL}/login${location.search}`}
                            style={{ color: TP_COLORS.tutopiyaGreenMain.light }}
                          >
                            Login here
                          </NavLink>
                        </Typography>
                      </Grid>
                    </Grid>
                  </form>
                </div>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default SignUp;
